import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Download, ArrowLeft, Loader2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageStudio() {
  const navigate = useNavigate();
  const [presets, setPresets] = useState({ styles: [], realism: [], moods: [], aspects: [], engines: [] });
  const [engine, setEngine] = useState("standard");
  const [prompt, setPrompt] = useState("");
  const [customDetails, setCustomDetails] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [style, setStyle] = useState("photorealistic");
  const [realism, setRealism] = useState("balanced");
  const [mood, setMood] = useState("neutral");
  const [aspect, setAspect] = useState("square");
  const [generating, setGenerating] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [results, setResults] = useState(() => {
    try {
      const cached = sessionStorage.getItem("image_studio_gallery");
      return cached ? JSON.parse(cached) : [];
    } catch (_) {
      return [];
    }
  }); // [{url, prompt, style, ts}]

  useEffect(() => {
    axios.get(`${API}/images/studio/presets`).then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (results.length === 0) return;
    try {
      sessionStorage.setItem("image_studio_gallery", JSON.stringify(results.slice(0, 12)));
    } catch (_) { /* ignore */ }
  }, [results]);

  const generate = async () => {
    if (!prompt.trim() || prompt.trim().length < 3) {
      toast.error("Please describe what you want to see (at least 3 characters)");
      return;
    }
    setGenerating(true);
    setStatusMsg("Crafting your prompt...");
    try {
      const createRes = await axios.post(`${API}/images/studio/generate`, {
        prompt: prompt.trim(),
        style, realism, mood, aspect, engine,
        custom_details: customDetails.trim() || null,
        negative_prompt: negativePrompt.trim() || null,
      });
      const jobId = createRes.data.job_id;
      setStatusMsg("Generating your image...");
      // Poll
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 2500));
        const job = (await axios.get(`${API}/images/jobs/${jobId}`)).data;
        if (job.status === "completed") {
          setResults((prev) => [
            {
              url: job.image_url,
              prompt: prompt.trim(),
              full_prompt: job.prompt_used,
              style, realism, mood, aspect, engine,
              ts: Date.now(),
            },
            ...prev,
          ].slice(0, 12));
          toast.success("Image ready!");
          setStatusMsg("");
          return;
        }
        if (job.status === "failed") {
          toast.error(job.error || "Image generation failed");
          setStatusMsg("");
          return;
        }
        setStatusMsg(
          job.status === "building_prompt" ? "Crafting your prompt..." :
          job.status === "generating" ? "Generating your image..." :
          "Working..."
        );
      }
      toast.error("Generation took too long. Please try again.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Failed to generate image");
    } finally {
      setGenerating(false);
      setStatusMsg("");
    }
  };

  const downloadImage = (url, ts) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = `shweebum-image-${ts}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="image-studio-back-btn"
              className="text-slate-300 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <div className="hidden sm:flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-fuchsia-400" />
              <h1 className="text-lg font-semibold">Image Studio</h1>
              <span className="text-xs text-slate-400 hidden md:inline">— Create any image you can describe</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-4 sm:p-6 grid gap-6 lg:grid-cols-[480px,1fr]">
        {/* LEFT: controls */}
        <div className="space-y-5">
          {/* Engine */}
          {presets.engines?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-slate-200">Engine</Label>
              <div className="grid grid-cols-2 gap-2">
                {presets.engines.map((e) => (
                  <button
                    key={e.key}
                    type="button"
                    onClick={() => e.available && setEngine(e.key)}
                    disabled={generating || !e.available}
                    data-testid={`engine-${e.key}`}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-colors ${
                      engine === e.key
                        ? e.key === "mature"
                          ? "bg-rose-500/20 border-rose-400 text-rose-200"
                          : "bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-200"
                        : e.available
                          ? "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                          : "bg-slate-900/20 border-slate-800 text-slate-600 cursor-not-allowed"
                    }`}
                  >
                    <span className="text-sm font-medium block">{e.label}</span>
                    <span className="text-[10px] block opacity-80 leading-tight mt-0.5">
                      {e.available ? e.description : "Not configured — fal.ai key required"}
                    </span>
                  </button>
                ))}
              </div>
              {engine === "mature" && (
                <p className="text-[11px] text-rose-300/80" data-testid="mature-engine-notice">
                  18+ engine: content filters are disabled. You're responsible for what you create — no minors, no real people.
                </p>
              )}
            </div>
          )}

          {/* Prompt */}
          <div className="space-y-2">
            <Label className="text-slate-200">What do you want to see?</Label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A grizzled detective in a rain-soaked alley, neon signs reflecting in the puddles, holding a half-burned cigarette..."
              rows={5}
              className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="image-studio-prompt"
              disabled={generating}
            />
            <p className="text-xs text-slate-500">Be as specific as you want. Include names, expressions, actions, lighting, camera angles — whatever matters.</p>
          </div>

          {/* Custom details / speech */}
          <div className="space-y-2">
            <Label className="text-slate-200">Extra details <span className="text-slate-500 text-xs">(optional — speech bubbles, signs, props)</span></Label>
            <Input
              value={customDetails}
              onChange={(e) => setCustomDetails(e.target.value)}
              placeholder='e.g. she is saying "the storm is coming", neon sign reads "OPEN 24H"'
              className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="image-studio-extras"
              disabled={generating}
            />
          </div>

          {/* Style */}
          <div className="space-y-2">
            <Label className="text-slate-200">Style</Label>
            <div className="grid grid-cols-3 gap-2">
              {presets.styles.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStyle(s.key)}
                  disabled={generating}
                  data-testid={`style-${s.key}`}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors ${
                    style === s.key
                      ? "bg-fuchsia-500/20 border-fuchsia-400 text-fuchsia-200"
                      : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Realism */}
          <div className="space-y-2">
            <Label className="text-slate-200">Realism</Label>
            <div className="grid grid-cols-4 gap-2">
              {presets.realism.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => setRealism(r.key)}
                  disabled={generating}
                  data-testid={`realism-${r.key}`}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors ${
                    realism === r.key
                      ? "bg-cyan-500/20 border-cyan-400 text-cyan-200"
                      : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mood */}
          <div className="space-y-2">
            <Label className="text-slate-200">Mood</Label>
            <div className="grid grid-cols-4 gap-2">
              {presets.moods.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setMood(m.key)}
                  disabled={generating}
                  data-testid={`mood-${m.key}`}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors ${
                    mood === m.key
                      ? "bg-emerald-500/20 border-emerald-400 text-emerald-200"
                      : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* Aspect */}
          <div className="space-y-2">
            <Label className="text-slate-200">Aspect ratio</Label>
            <div className="grid grid-cols-3 gap-2">
              {presets.aspects.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  onClick={() => setAspect(a.key)}
                  disabled={generating}
                  data-testid={`aspect-${a.key}`}
                  className={`text-xs px-2 py-2 rounded-lg border transition-colors ${
                    aspect === a.key
                      ? "bg-amber-500/20 border-amber-400 text-amber-200"
                      : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Negative */}
          <div className="space-y-2">
            <Label className="text-slate-200">Avoid <span className="text-slate-500 text-xs">(optional)</span></Label>
            <Input
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder="e.g. blurry, text artifacts, extra fingers"
              className="bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500"
              data-testid="image-studio-negative"
              disabled={generating}
            />
          </div>

          <Button
            onClick={generate}
            disabled={generating || !prompt.trim()}
            data-testid="image-studio-generate-btn"
            className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:from-fuchsia-400 hover:to-cyan-400 text-white shadow-lg shadow-fuchsia-500/30 h-12 text-base"
          >
            {generating ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {statusMsg || "Generating..."}</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" /> Generate Image</>
            )}
          </Button>
        </div>

        {/* RIGHT: results */}
        <div className="space-y-4">
          {results.length === 0 && !generating ? (
            <div className="rounded-2xl border-2 border-dashed border-slate-700 bg-slate-900/30 h-64 flex items-center justify-center text-slate-500" data-testid="image-studio-empty">
              <div className="text-center">
                <Wand2 className="w-10 h-10 mx-auto mb-3 opacity-60" />
                <p>Your generated images will appear here.</p>
              </div>
            </div>
          ) : (
            <>
              {generating && results.length === 0 && (
                <div className="rounded-2xl border border-slate-700 bg-slate-900/50 h-96 flex items-center justify-center" data-testid="image-studio-loading">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-fuchsia-400" />
                    <p className="text-slate-300">{statusMsg || "Generating..."}</p>
                  </div>
                </div>
              )}
              <div className="grid gap-4 sm:grid-cols-2">
                {results.map((r) => (
                  <div key={r.ts} className="rounded-2xl overflow-hidden border border-slate-700 bg-slate-900/40 group" data-testid={`image-result-${r.ts}`}>
                    <div className="relative">
                      <img
                        src={r.url}
                        alt={r.prompt}
                        className="w-full h-auto block"
                      />
                      <button
                        type="button"
                        onClick={() => downloadImage(r.url, r.ts)}
                        className="absolute top-2 right-2 bg-black/60 hover:bg-black text-white text-xs px-3 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                        data-testid={`image-download-${r.ts}`}
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                    <div className="p-3 space-y-1">
                      <p className="text-sm text-slate-200 line-clamp-2">{r.prompt}</p>
                      <div className="flex flex-wrap gap-1 text-[10px] text-slate-500">
                        <span className="px-1.5 py-0.5 rounded bg-slate-800">{r.style}</span>
                        <span className="px-1.5 py-0.5 rounded bg-slate-800">{r.realism}</span>
                        <span className="px-1.5 py-0.5 rounded bg-slate-800">{r.mood}</span>
                        <span className="px-1.5 py-0.5 rounded bg-slate-800">{r.aspect}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
